/**
 * As duas prateleiras que ficam gravadas: a fila de leitura de cada pessoa
 * (`read_list`) e a lista de livros da casa (`our_books`).
 *
 * Junta o que no Flutter estava em `OpenLibraryService` (a metade que grava) e
 * em `OurBooksService`. Aqui não há stream nem cache em disco: o `Armazem` já
 * é o cache, e a tela lê dele com `usarArmazem`.
 */

import type { Armazem } from '../armazem';
import type { Casa } from '../types/casa';
import {
  COLECAO_LEITURA,
  livroDeMapa,
  livroParaMapa,
  type Livro,
} from './livro';
import {
  COLECAO_NOSSOS_LIVROS,
  nossoLivroDeMapa,
  nossoLivroNovo,
  nossoLivroParaMapa,
  type NossoLivro,
} from './nossosLivros';

/** Quem pode mexer na lista da casa: as pessoas da casa, quaisquer que sejam. */
const daCasa = (casa: Casa | null | undefined, slug: string): boolean =>
  !!slug && (casa?.pessoas ?? []).some((p) => p.slug === slug);

/**
 * A identidade de um livro entre acervos. A obra do Open Library é a primeira
 * escolha; o item do Archive serve quando não há obra.
 *
 * O Flutter procurava só pela obra, e livro que veio do Archive ou do Gutenberg
 * não tem obra nenhuma: dois deles casavam pela chave vazia, e o segundo a ser
 * guardado voltava sendo o primeiro.
 */
const mesmoLivro = (
  a: { obra: string; iaId: string },
  b: { obra: string; iaId: string },
): boolean => {
  if (a.obra && b.obra) return a.obra === b.obra;
  if (a.iaId && b.iaId) return a.iaId === b.iaId;
  return false;
};

// ── A fila de cada pessoa: `read_list` ──────────────────────────────────

/** A fila de [quem], da mais recente para a mais antiga. */
export function filaDeLeitura(armazem: Armazem, quem: string): Livro[] {
  if (!quem) return [];
  return armazem
    .colecao(COLECAO_LEITURA)
    .map((d) => livroDeMapa(d.id, d.dados))
    .filter((l) => l.quem === quem)
    .sort((a, b) => b.quando.getTime() - a.quando.getTime());
}

export function acharNaFila(
  armazem: Armazem,
  livro: { obra: string; iaId: string },
  quem: string,
): Livro | null {
  if (!quem) return null;
  for (const d of armazem.colecao(COLECAO_LEITURA)) {
    const guardado = livroDeMapa(d.id, d.dados);
    if (guardado.quem === quem && mesmoLivro(guardado, livro)) return guardado;
  }
  return null;
}

/**
 * `saveToReadList`: guarda ou só muda o status.
 *
 * Quando o livro já está na fila, mesclam-se apenas `status` e `addedAt` — o
 * resto do documento é o que a busca trouxe da primeira vez, e pode estar mais
 * completo do que o que veio agora.
 */
export async function guardarNaFila(
  armazem: Armazem,
  livro: Livro,
  status: string,
  quem: string,
): Promise<void> {
  if (!quem) return;
  const jaTem = acharNaFila(armazem, livro, quem);
  if (jaTem) {
    await armazem.gravar(
      COLECAO_LEITURA,
      jaTem.id,
      { status, addedAt: new Date().toISOString() },
      true,
    );
    return;
  }
  const { id: _id, ...campos } = { ...livro, status, quem, quando: new Date() };
  await armazem.adicionar(COLECAO_LEITURA, livroParaMapa(campos));
}

export async function tirarDaFila(
  armazem: Armazem,
  livro: { obra: string; iaId: string },
  quem: string,
): Promise<void> {
  const guardado = acharNaFila(armazem, livro, quem);
  if (guardado) await armazem.apagar(COLECAO_LEITURA, guardado.id);
}

// ── A lista da casa: `our_books` ────────────────────────────────────────

/** A lista da casa inteira, da mais recente para a mais antiga. */
export function livrosDaCasa(armazem: Armazem): NossoLivro[] {
  return armazem
    .colecao(COLECAO_NOSSOS_LIVROS)
    .map((d) => nossoLivroDeMapa(d.id, d.dados))
    .sort((a, b) => b.quando.getTime() - a.quando.getTime());
}

/** Só o que uma pessoa pôs. A tela mostra uma fileira por pessoa da casa. */
export const livrosPostosPor = (armazem: Armazem, slug: string): NossoLivro[] =>
  slug ? livrosDaCasa(armazem).filter((l) => l.quemPos === slug) : [];

export function acharNaCasa(
  armazem: Armazem,
  livro: { obra: string; iaId: string },
): NossoLivro | null {
  for (const d of armazem.colecao(COLECAO_NOSSOS_LIVROS)) {
    const guardado = nossoLivroDeMapa(d.id, d.dados);
    if (mesmoLivro(guardado, livro)) return guardado;
  }
  return null;
}

/**
 * `addToOurBooks`. Devolve o livro que ficou na lista — o novo, ou o que já
 * estava lá, para a tela poder abrir os dois casos do mesmo jeito. Devolve
 * `null` quando quem pediu não é da casa.
 */
export async function porNaCasa(
  armazem: Armazem,
  livro: Livro,
  quemPos: string,
  casa?: Casa | null,
): Promise<NossoLivro | null> {
  if (!daCasa(casa, quemPos)) return null;
  const jaTem = acharNaCasa(armazem, livro);
  if (jaTem) return jaTem;
  const novo = nossoLivroNovo({
    obra: livro.obra,
    edicao: livro.edicao,
    iaId: livro.iaId,
    titulo: livro.titulo,
    autor: livro.autor,
    capa: livro.capa,
    ano: livro.ano,
    assuntos: livro.assuntos,
    quemPos,
    quando: new Date(),
  });
  const { id: _id, ...campos } = novo;
  const id = await armazem.adicionar(COLECAO_NOSSOS_LIVROS, nossoLivroParaMapa(campos));
  return { ...novo, id };
}

/**
 * `setReadFlag`: marca ou desmarca que alguém leu.
 *
 * Grava o documento inteiro, sem mesclar: tirar um nome de dentro do mapa de
 * quem leu é apagar uma chave aninhada, e mesclar por cima nunca apaga nada.
 */
export async function marcarLeitura(
  armazem: Armazem,
  livro: NossoLivro,
  slug: string,
  leu: boolean,
  casa?: Casa | null,
): Promise<void> {
  if (!daCasa(casa, slug)) return;
  const lidos = { ...livro.lidoPor };
  if (leu) lidos[slug] = new Date();
  else delete lidos[slug];
  const { id: _id, ...campos } = { ...livro, lidoPor: lidos };
  await armazem.gravar(COLECAO_NOSSOS_LIVROS, livro.id, nossoLivroParaMapa(campos));
}

export async function tirarDaCasa(armazem: Armazem, livro: NossoLivro): Promise<void> {
  if (livro.id) await armazem.apagar(COLECAO_NOSSOS_LIVROS, livro.id);
}
